import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import Header from "@/components/Header";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Clock, Save, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/utils/cn";

const DAYS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const TIME_SLOTS = [
  "06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00",
  "20:00", "21:00", "22:00", "23:00"
];

const defaultSchedule = DAYS.reduce((acc, day) => {
  acc[day.key] = { enabled: false, startTime: "09:00", endTime: "18:00" };
  return acc;
}, {});

export default function AvailabilityPage() {
  const { astrologer } = useAuth();
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load existing schedule
  useEffect(() => {
    if (astrologer?.availability) {
      setSchedule(prev => ({ ...prev, ...astrologer.availability }));
    }
  }, [astrologer]);

  const toggleDay = (dayKey) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], enabled: !prev[dayKey].enabled }
    }));
  };

  const updateTime = (dayKey, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      // TODO: API call to save schedule
      // await updateAvailability(astrologer.astrologerId, schedule, astrologer.accessToken);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setMessage({ type: "success", text: "Availability saved successfully" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to save availability" });
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <>
      <Header name="Availability" />
      <div className="w-full p-4 max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Weekly Schedule</h2>
              <p className="text-sm text-gray-500">Set your regular availability for consultations</p>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>

          {/* Message */}
          {message && (
            <div className={cn(
              "mb-4 p-3 rounded-lg flex items-center gap-2 text-sm",
              message.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
            )}>
              <AlertCircle className="h-4 w-4" />
              {message.text}
            </div>
          )}

          {/* Schedule Grid */}
          <div className="space-y-3">
            {DAYS.map(day => (
              <div 
                key={day.key}
                className={cn(
                  "flex items-center gap-4 p-3 rounded-lg transition-colors",
                  schedule[day.key].enabled ? "bg-orange-50" : "bg-gray-50"
                )}
              >
                {/* Day Toggle */}
                <div className="flex items-center gap-3 w-24">
                  <Switch
                    checked={schedule[day.key].enabled}
                    onCheckedChange={() => toggleDay(day.key)}
                    className="data-[state=checked]:bg-orange-500"
                  />
                  <span className={cn(
                    "font-medium text-sm",
                    schedule[day.key].enabled ? "text-gray-900" : "text-gray-400"
                  )}>
                    {day.label}
                  </span>
                </div>

                {/* Time Selection */}
                {schedule[day.key].enabled ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <select
                      value={schedule[day.key].startTime}
                      onChange={(e) => updateTime(day.key, "startTime", e.target.value)}
                      className="px-2 py-1 text-sm border rounded-md bg-white"
                    >
                      {TIME_SLOTS.map(time => (
                        <option key={time} value={time}>{formatTime(time)}</option>
                      ))}
                    </select>
                    <span className="text-gray-400">to</span>
                    <select
                      value={schedule[day.key].endTime}
                      onChange={(e) => updateTime(day.key, "endTime", e.target.value)}
                      className="px-2 py-1 text-sm border rounded-md bg-white"
                    >
                      {TIME_SLOTS.map(time => (
                        <option key={time} value={time}>{formatTime(time)}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Unavailable</span>
                )}
              </div>
            ))}
          </div>

          {/* Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> This sets your regular weekly schedule. Users can book 
              appointments during these times. You can still go online/offline using the toggle 
              in the sidebar for instant calls.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
